if (!customElements.get('volume-pricing-offer')) {
  class VolumePricingOffer extends HTMLElement {
    connectedCallback() {
      if (!this.hasValidConfiguration()) return;

      this.quantityInput = document.getElementById(`Quantity-${this.dataset.sectionId}`);
      this.onQuantityChange = this.update.bind(this);
      this.onTierClick = this.selectTier.bind(this);
      this.quantityInput?.addEventListener('change', this.onQuantityChange);
      this.quantityInput?.addEventListener('input', this.onQuantityChange);
      this.addEventListener('click', this.onTierClick);
      this.removeAttribute('hidden');
      this.update();
    }

    disconnectedCallback() {
      this.quantityInput?.removeEventListener('change', this.onQuantityChange);
      this.quantityInput?.removeEventListener('input', this.onQuantityChange);
      this.removeEventListener('click', this.onTierClick);
    }

    hasValidConfiguration() {
      const source = this.querySelector('[data-volume-pricing-configuration]');
      if (!source) return false;

      let configuration;
      try {
        configuration = JSON.parse(source.textContent);
      } catch (_error) {
        return false;
      }

      if (
        configuration?.version !== 1 ||
        configuration.enabled !== true ||
        configuration.evaluationScope !== 'line' ||
        configuration.currency !== this.dataset.currency ||
        !Array.isArray(configuration.tiers) ||
        configuration.tiers.length === 0
      ) {
        return false;
      }

      const normalPrice = Number.parseInt(this.dataset.normalPriceCents, 10) / 100;
      let previousMinimum = 1;
      let previousPrice = normalPrice;
      const moneyPattern = /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/;

      const valid = configuration.tiers.every((tier) => {
        if (
          !Number.isSafeInteger(tier?.minimumQuantity) ||
          tier.minimumQuantity < 2 ||
          tier.minimumQuantity <= previousMinimum ||
          typeof tier.unitPrice !== 'string' ||
          !moneyPattern.test(tier.unitPrice)
        ) {
          return false;
        }

        const price = Number(tier.unitPrice);
        if (!Number.isFinite(price) || price <= 0 || price > previousPrice || price >= normalPrice) {
          return false;
        }

        previousMinimum = tier.minimumQuantity;
        previousPrice = price;
        return true;
      });

      return valid && this.querySelectorAll('[data-volume-tier]').length === configuration.tiers.length + 1;
    }

    selectTier(event) {
      const tier = event.target.closest('[data-volume-tier]');
      if (!tier || !this.contains(tier) || !this.quantityInput) return;

      const requestedQuantity = Number.parseInt(tier.dataset.minimumQuantity, 10);
      if (!Number.isSafeInteger(requestedQuantity)) return;

      const minimum = Number.parseInt(this.quantityInput.min, 10);
      const maximum = Number.parseInt(this.quantityInput.max, 10);
      const step = Number.parseInt(this.quantityInput.step, 10);
      const safeMinimum = Number.isSafeInteger(minimum) ? minimum : 1;
      const safeStep = Number.isSafeInteger(step) && step > 0 ? step : 1;
      let quantity = Math.max(requestedQuantity, safeMinimum);

      if ((quantity - safeMinimum) % safeStep !== 0) {
        quantity = safeMinimum + Math.ceil((quantity - safeMinimum) / safeStep) * safeStep;
      }
      if (Number.isSafeInteger(maximum)) quantity = Math.min(quantity, maximum);

      this.quantityInput.value = String(quantity);
      this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    update() {
      const quantity = Math.max(1, Number.parseInt(this.quantityInput?.value || '1', 10));
      const tiers = [...this.querySelectorAll('[data-volume-tier]')]
        .map((element) => ({
          element,
          minimum: Number.parseInt(element.dataset.minimumQuantity, 10),
          priceCents: Number.parseInt(element.dataset.priceCents, 10),
          priceLabel: element.dataset.priceLabel,
        }))
        .filter((tier) => Number.isSafeInteger(tier.minimum))
        .sort((a, b) => a.minimum - b.minimum);

      if (!tiers.length) return;

      let activeIndex = 0;
      tiers.forEach((tier, index) => {
        if (quantity >= tier.minimum) activeIndex = index;
      });

      tiers.forEach((tier, index) => {
        const active = index === activeIndex;
        tier.element.classList.toggle('is-active', active);
        tier.element.setAttribute('aria-pressed', String(active));
      });

      const status = this.querySelector('[data-volume-pricing-status]');
      const progress = this.querySelector('[data-volume-pricing-progress]');
      const nextTier = tiers[activeIndex + 1];

      if (status) {
        status.textContent = activeIndex === 0
          ? 'Lleva más y paga menos'
          : nextTier
            ? '¡Ya tienes precio especial!'
            : '¡Mejor precio desbloqueado!';
      }

      if (progress) {
        if (!nextTier) {
          progress.textContent = 'Ya tienes el mejor precio disponible.';
        } else {
          const remaining = nextTier.minimum - quantity;
          progress.textContent = `Agrega ${remaining} más para pagar ${nextTier.priceLabel} c/u.`;
        }
      }
    }
  }

  customElements.define('volume-pricing-offer', VolumePricingOffer);
}

if (!window.volumePricingVariantListenerInstalled) {
  window.volumePricingVariantListenerInstalled = true;
  document.addEventListener('afterVariantChanged', (event) => {
    const sectionId = event.detail?.sectionId;
    const html = event.detail?.html;
    if (!sectionId || !html) return;

    const current = document.getElementById(`VolumePricingTiers-${sectionId}`);
    const updated = html.getElementById(`VolumePricingTiers-${sectionId}`);
    if (!current || !updated) return;

    current.replaceWith(document.importNode(updated, true));
  });
}

if (!customElements.get('volume-pricing-mix-builder')) {
  class VolumePricingMixBuilder extends HTMLElement {
    connectedCallback() {
      if (!this.loadConfiguration()) return;

      this.choices = [...this.querySelectorAll('[data-mix-choice]')];
      this.tierElements = [...this.querySelectorAll('[data-mix-tier]')]
        .map((element, index) => {
          const configuredPrice = Number(this.configuration.tiers[index]?.unitPrice);
          const renderedPriceCents = Number.parseInt(element.dataset.priceCents, 10);

          return {
            element,
            minimum: Number.parseInt(element.dataset.minimumQuantity, 10),
            priceLabel: element.dataset.priceLabel,
            priceCents: Number.isSafeInteger(renderedPriceCents)
              ? renderedPriceCents
              : Math.round(configuredPrice * 100),
          };
        })
        .sort((a, b) => a.minimum - b.minimum);

      if (!this.prepareVariants() || !this.validTiers()) return;
      this.onClick = this.handleClick.bind(this);
      this.onSubmit = this.addCombination.bind(this);
      this.onVariantChanged = this.handleVariantChanged.bind(this);
      this.onVariantSelection = this.handleVariantSelection.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onExternalOpen = this.handleExternalOpen.bind(this);
      this.addEventListener('click', this.onClick);
      this.querySelector('[data-mix-submit]')?.addEventListener('click', this.onSubmit);
      document.addEventListener('afterVariantChanged', this.onVariantChanged);
      document.addEventListener('change', this.onVariantSelection);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onExternalOpen);
      this.removeAttribute('hidden');
      this.updateEligibilityNote(this.currentVariantId());
      window.setTimeout(() => this.updateEligibilityNote(this.currentVariantId()), 150);
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.querySelector('[data-mix-submit]')?.removeEventListener('click', this.onSubmit);
      document.removeEventListener('afterVariantChanged', this.onVariantChanged);
      document.removeEventListener('change', this.onVariantSelection);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onExternalOpen);
      window.clearTimeout(this.tierGuideTimer);
      document.documentElement.classList.remove('volume-pricing-dialog-open');
    }

    loadConfiguration() {
      const source = this.querySelector('[data-volume-pricing-configuration]');
      if (!source) return false;

      try {
        this.configuration = JSON.parse(source.textContent);
      } catch (_error) {
        return false;
      }

      const eligible = this.configuration?.eligibleVariants;
      return (
        this.configuration?.version === 2 &&
        this.configuration.enabled === true &&
        this.configuration.evaluationScope === 'product' &&
        this.configuration.currency === this.dataset.currency &&
        Array.isArray(this.configuration.tiers) &&
        this.configuration.tiers.length > 0 &&
        (eligible?.mode === 'all' || (eligible?.mode === 'selected' && Array.isArray(eligible.variantIds)))
      );
    }

    validTiers() {
      let previousMinimum = 1;
      return this.tierElements.length === this.configuration.tiers.length && this.configuration.tiers.every((tier) => {
        const valid = Number.isSafeInteger(tier?.minimumQuantity) &&
          tier.minimumQuantity > previousMinimum &&
          typeof tier.unitPrice === 'string' &&
          /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(tier.unitPrice) &&
          Number(tier.unitPrice) > 0;
        previousMinimum = tier?.minimumQuantity;
        return valid;
      });
    }

    prepareVariants() {
      const eligibility = this.configuration.eligibleVariants;
      const selectedIds = new Set(eligibility.variantIds || []);
      let eligibleCount = 0;

      this.choices.forEach((choice, originalIndex) => {
        choice.dataset.originalIndex = String(originalIndex);
        const eligible = eligibility.mode === 'all' || selectedIds.has(choice.dataset.variantGid);
        choice.dataset.eligible = String(eligible);
        choice.hidden = !eligible;
        choice.dataset.quantity = '0';
        if (!eligible) return;

        eligibleCount += 1;
        const maximum = this.maximumFor(choice);
        const available = choice.dataset.available === 'true' && maximum !== 0;
        choice.dataset.available = String(available);
        choice.disabled = !available;
        choice.querySelector('[data-mix-sold-out]').hidden = available;
      });

      this.sortChoicesByInventory();

      return eligibleCount > 0;
    }

    sortChoicesByInventory() {
      const choices = this.querySelector('[data-mix-choices]');
      if (!choices) return;

      this.choices.sort((left, right) => {
        const leftEligible = left.dataset.eligible === 'true';
        const rightEligible = right.dataset.eligible === 'true';
        if (leftEligible !== rightEligible) return leftEligible ? -1 : 1;

        const leftAvailable = left.dataset.available === 'true';
        const rightAvailable = right.dataset.available === 'true';
        if (leftAvailable !== rightAvailable) return leftAvailable ? -1 : 1;

        if (leftAvailable && rightAvailable) {
          const leftManaged = Object.hasOwn(left.dataset, 'maximumQuantity');
          const rightManaged = Object.hasOwn(right.dataset, 'maximumQuantity');
          if (leftManaged !== rightManaged) return leftManaged ? -1 : 1;

          if (leftManaged && rightManaged) {
            const stockDifference = this.maximumFor(right) - this.maximumFor(left);
            if (stockDifference !== 0) return stockDifference;
          }
        }

        return Number.parseInt(left.dataset.originalIndex, 10) - Number.parseInt(right.dataset.originalIndex, 10);
      });

      choices.append(...this.choices);
    }

    maximumFor(choice) {
      if (!Object.hasOwn(choice.dataset, 'maximumQuantity')) return Number.POSITIVE_INFINITY;
      const maximum = Number.parseInt(choice.dataset.maximumQuantity, 10);
      return Number.isSafeInteger(maximum) && maximum >= 0 ? maximum : 0;
    }

    handleExternalOpen(event) {
      const opener = event.target.closest('[data-mix-open-external]');
      if (!opener || opener.dataset.mixSectionId !== this.dataset.sectionId) return;
      this.openDialog(opener);
    }

    handleClick(event) {
      if (event.target.closest('[data-mix-open]')) {
        this.openDialog(event.target.closest('[data-mix-open]'));
        return;
      }
      if (event.target.closest('[data-mix-close]')) {
        this.closeDialog();
        return;
      }
      if (event.target.closest('[data-mix-clear]')) {
        this.choices.forEach((choice) => { choice.dataset.quantity = '0'; });
        this.update();
        return;
      }
      const tierButton = event.target.closest('[data-mix-tier]');
      if (tierButton) {
        this.handleTierIntent(tierButton);
        return;
      }
      const button = event.target.closest('[data-mix-choice], [data-mix-increment], [data-mix-decrement], [data-mix-remove]');
      if (!button || !this.contains(button)) return;

      const variantId = button.dataset.variantId || button.closest('[data-variant-id]')?.dataset.variantId;
      const choice = this.choices.find((item) => item.dataset.variantId === variantId);
      if (!choice) return;
      const current = Number.parseInt(choice.dataset.quantity || '0', 10);
      const maximum = this.maximumFor(choice);
      const next = button.matches('[data-mix-remove]')
        ? 0
        : button.matches('[data-mix-choice], [data-mix-increment]')
        ? Math.min(current + 1, maximum)
        : Math.max(current - 1, 0);

      choice.dataset.quantity = String(next);
      this.update();
    }

    update() {
      this.setTierFeedback('');
      const selectedChoices = this.choices.filter((choice) => choice.dataset.eligible === 'true' && Number.parseInt(choice.dataset.quantity || '0', 10) > 0);
      const total = selectedChoices.reduce((sum, choice) => sum + Number.parseInt(choice.dataset.quantity, 10), 0);
      const compatibleTiers = this.tierElements.filter((tier) => selectedChoices.every((choice) => Number.parseInt(choice.dataset.priceCents, 10) > tier.priceCents));
      const activeTier = [...compatibleTiers].reverse().find((tier) => total >= tier.minimum);
      const nextTier = compatibleTiers.find((tier) => total < tier.minimum);
      const selected = this.querySelector('[data-mix-selected]');
      selected.replaceChildren();
      this.choices.forEach((choice) => {
        if (choice.dataset.eligible !== 'true') return;
        const quantity = Number.parseInt(choice.dataset.quantity || '0', 10);
        choice.classList.toggle('is-selected', quantity > 0);
        choice.setAttribute('aria-pressed', String(quantity > 0));
        const count = choice.querySelector('[data-mix-choice-count]');
        count.textContent = String(quantity);
        count.hidden = quantity === 0;
        if (quantity > 0) selected.append(this.selectedRow(choice, quantity, activeTier));
      });
      this.querySelector('[data-mix-combination]').hidden = total === 0;

      this.tierElements.forEach((tier) => tier.element.classList.toggle('is-active', tier === activeTier));

      this.querySelector('[data-mix-total]').textContent = String(total);
      this.querySelector('[data-mix-total-visual]').textContent = String(total);
      this.querySelector('[data-mix-total-label]').textContent = total === 1 ? 'unidad seleccionada' : 'unidades seleccionadas';

      const status = this.querySelector('[data-mix-status]');
      const progress = this.querySelector('[data-mix-progress]');
      if (total === 0) {
        status.textContent = 'Elige tus favoritos para comenzar';
      } else if (!activeTier) {
        status.textContent = nextTier && nextTier.minimum - total === 1 ? '¡Te falta solo 1!' : 'Tu combinación está en progreso';
      } else if (nextTier) {
        status.textContent = total === activeTier.minimum ? '¡Ya desbloqueaste precio especial!' : 'Ya estás ahorrando';
      } else {
        status.textContent = '¡Desbloqueaste el mejor precio!';
      }

      if (nextTier && activeTier) {
        progress.textContent = `Ahora pagas ${activeTier.priceLabel} c/u. Agrega ${nextTier.minimum - total} más para llegar a ${nextTier.priceLabel} c/u.`;
      } else if (nextTier) {
        progress.textContent = `Agrega ${nextTier.minimum - total} más para pagar ${nextTier.priceLabel} c/u.`;
      } else {
        progress.textContent = activeTier ? `Ahora pagas ${activeTier.priceLabel} c/u.` : '';
      }

      const normalTotal = selectedChoices.reduce((sum, choice) => sum + Number.parseInt(choice.dataset.priceCents, 10) * Number.parseInt(choice.dataset.quantity, 10), 0);
      const effectiveTotal = activeTier ? activeTier.priceCents * total : normalTotal;
      const savings = Math.max(0, normalTotal - effectiveTotal);
      const totalIsReliable = Number.isSafeInteger(effectiveTotal) && effectiveTotal >= 0;
      const totals = this.querySelector('[data-mix-totals]');
      totals.hidden = total === 0;
      this.querySelector('[data-mix-effective-price]').textContent = activeTier ? `${this.money(activeTier.priceCents)} c/u` : 'Precio normal';
      this.querySelector('[data-mix-savings]').textContent = this.money(savings);
      this.querySelector('[data-mix-estimated-total]').textContent = totalIsReliable ? this.money(effectiveTotal) : '—';

      const quantityLabel = `${total} ${total === 1 ? 'unidad' : 'unidades'}`;
      this.querySelector('[data-mix-submit-label]').textContent = total > 0
        ? totalIsReliable
          ? `Agregar ${quantityLabel} · ${this.money(effectiveTotal)}`
          : `Agregar ${quantityLabel} al carrito`
        : 'Agregar combinación al carrito';
      const validSelection = selectedChoices.every((choice) => {
        const variantId = Number.parseInt(choice.dataset.variantId, 10);
        const quantity = Number.parseInt(choice.dataset.quantity, 10);
        return Number.isSafeInteger(variantId) && Number.isSafeInteger(quantity) && quantity > 0;
      });
      this.querySelector('[data-mix-submit]').disabled = total === 0 || !validSelection || this.submitting;
    }

    selectedRow(choice, quantity, activeTier) {
      const row = document.createElement('div');
      row.className = 'volume-pricing-mix__selected-row';
      row.dataset.variantId = choice.dataset.variantId;
      const media = choice.querySelector('.volume-pricing-mix__choice-media').cloneNode(true);
      media.querySelector('[data-mix-choice-count]')?.remove();
      row.append(media);
      const copy = document.createElement('div');
      copy.className = 'volume-pricing-mix__selected-copy';
      const name = document.createElement('strong');
      name.textContent = choice.dataset.variantTitle;
      const prices = document.createElement('span');
      prices.className = 'volume-pricing-mix__selected-prices';
      if (activeTier) {
        const normal = document.createElement('s');
        normal.textContent = choice.dataset.priceLabel;
        const effective = document.createElement('strong');
        effective.textContent = `${activeTier.priceLabel} c/u`;
        prices.append(normal, effective);
      } else {
        prices.textContent = `${choice.dataset.priceLabel} c/u`;
      }
      copy.append(name, prices);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'volume-pricing-mix__remove';
      remove.dataset.mixRemove = '';
      remove.dataset.variantId = choice.dataset.variantId;
      remove.textContent = 'Quitar';
      remove.setAttribute('aria-label', `Quitar ${choice.dataset.variantTitle} de tu combinación`);
      copy.append(remove);
      row.append(copy);
      const stepper = document.createElement('div');
      stepper.className = 'volume-pricing-mix__quantity';
      stepper.setAttribute('role', 'group');
      stepper.setAttribute('aria-label', `Cantidad de ${choice.dataset.variantTitle}`);
      stepper.innerHTML = `<button type="button" data-mix-decrement aria-label="Quitar una unidad">−</button><output>${quantity}</output><button type="button" data-mix-increment aria-label="Agregar una unidad" ${quantity >= this.maximumFor(choice) ? 'disabled' : ''}>+</button>`;
      row.append(stepper);
      return row;
    }

    handleTierIntent(tierButton) {
      const tier = this.tierElements.find((item) => item.element === tierButton);
      if (!tier) return;
      const total = this.choices.reduce((sum, choice) => sum + Number.parseInt(choice.dataset.quantity || '0', 10), 0);
      const remaining = tier.minimum - total;
      const highestTier = this.tierElements[this.tierElements.length - 1];

      if (remaining <= 0) {
        this.setTierFeedback(tier === highestTier
          ? 'Ya tienes el mejor precio disponible.'
          : 'Este precio ya está desbloqueado.');
        return;
      }

      this.setTierFeedback(`Te ${remaining === 1 ? 'falta' : 'faltan'} ${remaining} ${remaining === 1 ? 'unidad' : 'unidades'} para desbloquear ${tier.priceLabel} c/u.`);
      const heading = this.querySelector('[data-mix-choices-heading]');
      const choices = this.querySelector('[data-mix-choices]');
      choices.classList.remove('is-tier-guided');
      window.requestAnimationFrame(() => choices.classList.add('is-tier-guided'));
      window.clearTimeout(this.tierGuideTimer);
      this.tierGuideTimer = window.setTimeout(() => choices.classList.remove('is-tier-guided'), 1400);
      heading.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
      heading.focus({ preventScroll: true });
    }

    setTierFeedback(message) {
      const feedback = this.querySelector('[data-mix-tier-feedback]');
      const summaryFeedback = this.querySelector('[data-mix-tier-summary-feedback]');
      if (feedback) feedback.textContent = message;
      if (summaryFeedback) summaryFeedback.textContent = message;
    }

    money(cents) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.dataset.currency }).format(cents / 100);
    }

    openDialog(opener) {
      this.opener = opener;
      const overlay = this.querySelector('[data-mix-overlay]');
      if (!overlay.open) overlay.showModal();
      document.documentElement.classList.add('volume-pricing-dialog-open');
      window.requestAnimationFrame(() => {
        overlay.classList.add('is-open');
        this.querySelector('[data-mix-dialog]')?.focus();
      });
    }

    closeDialog() {
      const overlay = this.querySelector('[data-mix-overlay]');
      if (!overlay.open) return;
      overlay.classList.remove('is-open');
      overlay.close();
      document.documentElement.classList.remove('volume-pricing-dialog-open');
      this.opener?.focus();
    }

    handleKeydown(event) {
      const overlay = this.querySelector('[data-mix-overlay]');
      if (!overlay?.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...overlay.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((element) => !element.hidden);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    async addCombination() {
      if (this.submitting) return;
      const items = this.choices
        .filter((choice) => choice.dataset.eligible === 'true')
        .map((choice) => ({
          id: Number.parseInt(choice.dataset.variantId, 10),
          quantity: Number.parseInt(choice.dataset.quantity || '0', 10),
        }))
        .filter((item) => Number.isSafeInteger(item.id) && item.quantity > 0);

      if (!items.length) return;

      this.setSubmitting(true);
      this.showError('');
      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const submit = this.querySelector('[data-mix-submit]');
        const requestBody = { items };

        if (cartDrawer?.getSectionsToRender) {
          requestBody.sections = [...new Set(cartDrawer.getSectionsToRender().map((section) => section.id))];
          requestBody.sections_url = `${window.location.pathname}${window.location.search}`;
          cartDrawer.setActiveElement?.(submit);
        }

        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(requestBody),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload.status) {
          throw new Error(payload.description || 'No pudimos agregar la combinación al carrito.');
        }

        if (!cartDrawer?.renderContents || !payload.sections) {
          throw new Error('La combinación se agregó, pero no pudimos actualizar el carrito lateral. Recarga la página para verlo.');
        }

        cartDrawer.renderContents(payload);
        if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');

        this.closeDialog();
        this.choices.forEach((choice) => { choice.dataset.quantity = '0'; });
        this.update();
        if (cartDrawer.dataset.cartAction !== 'true') {
          window.setTimeout(() => cartDrawer.open(submit), 0);
        }

        document.dispatchEvent(new CustomEvent('afterCartChanged', { detail: {
          productVariantId: items[0]?.id,
          quantity: items.reduce((total, item) => total + item.quantity, 0),
          source: 'volume-pricing-mix',
          cartData: payload,
          target: this,
        } }));
        this.setSubmitting(false);
      } catch (error) {
        this.showError(error.message || 'No pudimos agregar la combinación al carrito. Intenta de nuevo.');
        this.setSubmitting(false);
      }
    }

    setSubmitting(submitting) {
      this.submitting = submitting;
      const submit = this.querySelector('[data-mix-submit]');
      submit?.classList.toggle('loading', submitting);
      submit?.setAttribute('aria-busy', String(submitting));
      this.querySelector('[data-mix-spinner]')?.classList.toggle('hidden', !submitting);
      this.update();
    }

    showError(message) {
      const error = this.querySelector('[data-mix-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }

    currentVariantId() {
      const urlId = new URL(window.location.href).searchParams.get('variant');
      const form = document.getElementById(`product-form-${this.dataset.sectionId}`) ||
        document.querySelector(`form[action*="/cart/add"] [name="id"]`)?.form;
      const id = urlId || form?.querySelector('[name="id"]')?.value;
      return id ? `gid://shopify/ProductVariant/${id}` : null;
    }

    handleVariantChanged(event) {
      const variantId = event.detail?.variant?.id || event.detail?.variantId;
      window.requestAnimationFrame(() => {
        this.updateEligibilityNote(variantId ? `gid://shopify/ProductVariant/${variantId}` : this.currentVariantId());
      });
    }

    handleVariantSelection(event) {
      if (!event.target.matches('input[type="radio"], select')) return;
      window.setTimeout(() => this.updateEligibilityNote(this.currentVariantId()), 100);
    }

    updateEligibilityNote(variantGid) {
      const note = this.parentElement?.querySelector('[data-volume-pricing-eligibility-note]');
      if (!note || !variantGid) return;
      const choice = this.choices.find((candidate) => candidate.dataset.variantGid === variantGid);
      note.hidden = !choice || choice.dataset.eligible === 'true';
    }
  }

  customElements.define('volume-pricing-mix-builder', VolumePricingMixBuilder);
}
